import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useActingOrg } from '@/auth/actingOrgContext';
import {
    fetchOrganization,
    fetchOrganizations,
    fetchOrgLevels,
    fetchScopedAccounts,
    type Organization,
    type ScopedAccountIssue,
    type ScopedAccountRow,
    type ScopedAccountsParams,
} from '@/api/orgAdmin';
import { filterParams, orderingParam } from './scopedAccountsQuery';
import { useScopedAccountFacets } from './useScopedAccountFacets';
import {
    EMPTY_FILTERS,
    type FacetGroupKey,
    type ScopedAccountSortField,
    type ScopedAccountsFilters,
    type SortDirection,
} from './types';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

/** Server ceiling (`emg_shared.api.common.paginate_queryset`), not a preference. */
const MAX_SERVER_PAGE_SIZE = 100;

/**
 * EAA-1839: cap on the "Chọn tất cả M dòng khớp bộ lọc" walk. Same shape and same
 * reason as `accounts/useAccountsScan.MAX_SCAN_PAGES` and
 * `roleMap/useAccountCounts` — the server caps a page at 100 rows and offers no
 * "give me every id" endpoint, so selecting a whole filtered set means walking
 * pages. 20 × 100 = 2,000 rows; past that the walk stops and says so instead of
 * hanging the console on a very large đơn vị.
 */
const MAX_SELECT_ALL_PAGES = 20;

/**
 * EAA-1839: view model behind `/console/hierarchy/accounts` — the org-scoped
 * account browser of frame 34, plus everything the bulk Level-assign dialog
 * (frame 35, `useBulkLevelAssign`) needs handed to it.
 *
 * SCOPE COMES FROM THE URL (`?org=<uuid>`), falling back to the acting org. That
 * is what makes the "Xem tài khoản" action on a tree node a real deep link
 * instead of a page that ignores which đơn vị was selected.
 *
 * SERVER-SIDE, NOT CLIENT-SIDE. Unlike `/console/accounts` (which walks the whole
 * realm because its endpoint has no filters at all), every filter here IS a real
 * query param, so paging and filtering are the server's job and one page is one
 * request. The two client-side exceptions are both forced and both flagged in the
 * UI: the facet counts (no aggregate endpoint exists — one `page_size=1` request
 * per option) and "select all matching" (no bulk-id endpoint exists — a capped
 * page walk).
 *
 * PERMISSIONS mirror `useHierarchyPage` exactly, because this is the same
 * `/console/hierarchy` surface: `console.orgs.view` to read, and
 * `org:{actingOrgId}.manage` to write (bulk Level-assign, revoking a grant) —
 * the same gate the assignment-authoring endpoints enforce. The capability is
 * keyed to the ACTING org while the scope may be a descendant of it; portal-api
 * re-authorizes the scope org on every call (`_visible_org_or_none`, which is
 * `OrganizationAdmin`-row based), so a descendant the caller does not administer
 * answers 404 and the screen renders that error rather than an empty table.
 */
export function useScopedAccounts() {
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const acting = useActingOrg();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    const actingOrgId = acting?.actingOrgId ?? null;
    const orgId = searchParams.get('org') ?? actingOrgId;

    const canView = can('console.orgs.view');
    const enabled = authenticated && Boolean(accessToken) && Boolean(orgId) && canView;
    const canManage = Boolean(actingOrgId) && can(`org:${actingOrgId}.manage`);
    const canReset = can('console.accounts.reset');

    /* ------------------------------------------------------------ scope org */

    // Same query key as `useOrgTree`'s own root fetch, so navigating here from the
    // tree reuses the row already in cache instead of re-requesting it.
    const orgQuery = useQuery({
        queryKey: ['organization', accessToken, orgId],
        queryFn: () => fetchOrganization(config.apiBase, accessToken as string, orgId as string),
        enabled,
        retry: false,
    });

    const childrenQuery = useQuery({
        queryKey: ['org-children', accessToken, orgId],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                parentId: orgId,
                pageSize: MAX_SERVER_PAGE_SIZE,
                ordering: 'name',
            }),
        enabled,
        retry: false,
    });

    const levelsQuery = useQuery({
        queryKey: ['org-levels', accessToken, orgId],
        queryFn: () => fetchOrgLevels(config.apiBase, accessToken as string, orgId as string),
        enabled,
        retry: false,
    });

    const org = orgQuery.data ?? null;
    const childOrgs = useMemo<Organization[]>(
        () => childrenQuery.data?.results ?? [],
        [childrenQuery.data]
    );
    const activeLevels = useMemo(
        () => (levelsQuery.data?.results ?? []).filter((level) => level.is_active),
        [levelsQuery.data]
    );

    /** id → name for the "Đơn vị" column. Only the scope org and its direct children
     *  are known here; a deeper descendant falls back to a shortened id. */
    const orgNameById = useMemo(() => {
        const map = new Map<string, string>();
        if (org) map.set(org.id, org.name);
        for (const child of childOrgs) map.set(child.id, child.name);
        return map;
    }, [org, childOrgs]);

    /* --------------------------------------------------------- filter state */

    const [filters, setFilters] = useState<ScopedAccountsFilters>(EMPTY_FILTERS);
    const [sortField, setSortField] = useState<ScopedAccountSortField>('granted_at');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(PAGE_SIZE_OPTIONS[0]);

    const filtersActive =
        filters.issue !== null ||
        filters.levelId !== null ||
        filters.childOrgId !== null ||
        filters.isActive !== null;

    const patchFilters = useCallback((patch: Partial<ScopedAccountsFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
        setPage(1);
    }, []);

    const toggleIncludeChildren = useCallback(() => {
        setFilters((prev) => ({
            ...prev,
            includeChildren: !prev.includeChildren,
            // EAA-1839: `child_organization_id` is only legal inside the effective
            // scope — with `include_children` off that scope is the single org, so a
            // child id would make the server return `Assignment.objects.none()`
            // (its deliberate fail-closed). Clear it with the toggle rather than
            // leaving a filter that silently empties the table.
            childOrgId: prev.includeChildren ? null : prev.childOrgId,
        }));
        setPage(1);
    }, []);

    const selectIssue = useCallback(
        (value: ScopedAccountIssue) =>
            patchFilters({ issue: filters.issue === value ? null : value }),
        [filters.issue, patchFilters]
    );
    const selectLevel = useCallback(
        (value: string) => patchFilters({ levelId: filters.levelId === value ? null : value }),
        [filters.levelId, patchFilters]
    );
    const selectChildOrg = useCallback(
        (value: string) =>
            patchFilters({ childOrgId: filters.childOrgId === value ? null : value }),
        [filters.childOrgId, patchFilters]
    );
    const selectStatus = useCallback(
        (value: boolean) => patchFilters({ isActive: filters.isActive === value ? null : value }),
        [filters.isActive, patchFilters]
    );
    const clearFilters = useCallback(() => {
        setFilters((prev) => ({ ...EMPTY_FILTERS, includeChildren: prev.includeChildren }));
        setPage(1);
    }, []);

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
        setPage(1);
    }, []);

    const toggleSort = useCallback(
        (field: ScopedAccountSortField) => {
            if (field === sortField) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                return;
            }
            setSortField(field);
            setSortDirection(field === 'granted_at' ? 'desc' : 'asc');
        },
        [sortField]
    );

    /* ------------------------------------------------------------ the list */

    const listParams = useMemo<ScopedAccountsParams>(
        () => ({
            ...filterParams(filters),
            page,
            pageSize,
            ordering: orderingParam(sortField, sortDirection),
        }),
        [filters, page, pageSize, sortField, sortDirection]
    );

    const listQuery = useQuery({
        queryKey: ['scoped-accounts', accessToken, orgId, listParams],
        queryFn: () =>
            fetchScopedAccounts(config.apiBase, accessToken as string, orgId as string, listParams),
        enabled,
        retry: false,
    });

    const rows = useMemo<ScopedAccountRow[]>(() => listQuery.data?.results ?? [], [listQuery.data]);
    const totalCount = listQuery.data?.count ?? 0;
    const totalPages = Math.max(1, listQuery.data?.total_pages ?? 1);
    const rangeFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeTo = Math.min(page * pageSize, totalCount);

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['scoped-accounts'] });
        void queryClient.invalidateQueries({ queryKey: ['scoped-accounts-facet'] });
    }, [queryClient]);

    /* ------------------------------------------------------- facet counting */

    // One `page_size=1` request per sidebar option — see `useScopedAccountFacets`
    // for why there is no aggregate endpoint to ask instead.
    const facetGroups = useScopedAccountFacets({
        apiBase: config.apiBase,
        token: accessToken,
        enabled,
        orgId,
        orgName: org?.name ?? null,
        childOrgs,
        activeLevels,
        filters,
    });

    const selectFacet = useCallback(
        (group: FacetGroupKey, key: string) => {
            if (group === 'issue') {
                selectIssue(key as ScopedAccountIssue);
                return;
            }
            if (group === 'level') {
                selectLevel(key);
                return;
            }
            if (group === 'childOrg') {
                selectChildOrg(key);
                return;
            }
            selectStatus(key === 'true');
        },
        [selectIssue, selectLevel, selectChildOrg, selectStatus]
    );

    /* ----------------------------------------------------------- selection */

    /**
     * Keyed by ASSIGNMENT id and holding the whole row, not just an id: the bulk
     * endpoints take `user_subs`, and "Chọn tất cả M dòng khớp bộ lọc" holds rows
     * that are not on the current page, so the rows have to be carried rather than
     * looked up in `rows`.
     */
    const [selected, setSelected] = useState<Map<string, ScopedAccountRow>>(new Map());
    const [selectAllBusy, setSelectAllBusy] = useState(false);
    const [selectAllCapped, setSelectAllCapped] = useState(false);

    const clearSelection = useCallback(() => {
        setSelected(new Map());
        setSelectAllCapped(false);
    }, []);

    // EAA-1839: a selection is dropped whenever the scope or any filter changes.
    // Pruning to the visible page (the `useRoleMapTable` rule) is wrong here —
    // that would silently undo "Chọn tất cả M dòng khớp bộ lọc", whose whole point
    // is holding rows off-page. Keeping it would be worse: it would let a bulk
    // action run against rows the operator can no longer see under the new filter.
    const filtersKey = JSON.stringify(filters);
    useEffect(() => {
        clearSelection();
    }, [orgId, filtersKey, clearSelection]);

    const toggleRow = useCallback((row: ScopedAccountRow) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(row.id)) next.delete(row.id);
            else next.set(row.id, row);
            return next;
        });
    }, []);

    const pageSelectedCount = rows.filter((row) => selected.has(row.id)).length;
    const allPageSelected = rows.length > 0 && pageSelectedCount === rows.length;
    const somePageSelected = pageSelectedCount > 0 && !allPageSelected;

    const toggleAll = useCallback(() => {
        setSelected((prev) => {
            const next = new Map(prev);
            const everySelected = rows.length > 0 && rows.every((row) => next.has(row.id));
            for (const row of rows) {
                if (everySelected) next.delete(row.id);
                else next.set(row.id, row);
            }
            return next;
        });
    }, [rows]);

    const selectAllMatching = useCallback(async () => {
        if (!orgId || !accessToken) return;
        setSelectAllBusy(true);
        setSelectAllCapped(false);
        const collected = new Map<string, ScopedAccountRow>();
        let walkPage = 1;
        let capped = false;
        try {
            for (;;) {
                const res = await fetchScopedAccounts(config.apiBase, accessToken, orgId, {
                    ...filterParams(filters),
                    page: walkPage,
                    pageSize: MAX_SERVER_PAGE_SIZE,
                    ordering: orderingParam(sortField, sortDirection),
                });
                for (const row of res.results) collected.set(row.id, row);
                const reachedEnd = walkPage >= res.total_pages;
                const hitCap = walkPage >= MAX_SELECT_ALL_PAGES;
                if (reachedEnd || hitCap) {
                    capped = hitCap && !reachedEnd;
                    break;
                }
                walkPage += 1;
            }
            setSelected(collected);
            setSelectAllCapped(capped);
        } finally {
            setSelectAllBusy(false);
        }
    }, [orgId, accessToken, config.apiBase, filters, sortField, sortDirection]);

    const selectedRows = useMemo(() => [...selected.values()], [selected]);

    /**
     * EAA-1839: the subs a bulk Level-assign may legally target — those whose
     * grant lives in the SCOPE org itself.
     *
     * This exclusion is not defensive tidiness, it prevents a silent double-grant.
     * `AssignmentService.bulk_preview` classifies against
     * `Assignment.objects.filter(organization_id=org_id, ...)` — the URL org only,
     * with no `include_children` recursion (its own comment: "Deliberately
     * single-org"). A row belonging to a descendant đơn vị therefore looks like
     * `new`, and `bulk_apply` would CREATE a second active grant in the parent org
     * while leaving the descendant grant untouched, rather than changing the level
     * the operator was looking at. The duplicate guard does not catch it either —
     * `_has_active_duplicate` keys on (user_sub, level_id), and these are two
     * different levels. So cross-đơn-vị rows are excluded and counted out loud.
     */
    const scopeSelectedRows = useMemo(
        () => selectedRows.filter((row) => row.organization_id === orgId),
        [selectedRows, orgId]
    );
    const bulkUserSubs = useMemo(
        () => [...new Set(scopeSelectedRows.map((row) => row.user_sub))],
        [scopeSelectedRows]
    );
    const crossOrgSelectedCount = selectedRows.length - scopeSelectedRows.length;

    /* ----------------------------------------------- honest-gap explanations */

    /** `null` when bulk Level-assign is available; otherwise why it is not. */
    let bulkLevelDisabledReason: string | null = null;
    if (!canManage) {
        bulkLevelDisabledReason = t(
            'console.hierarchy.scopedAccounts.bulkLevel.noManagePermission'
        );
    } else if (activeLevels.length === 0) {
        bulkLevelDisabledReason = t(
            'console.hierarchy.scopedAccounts.bulkLevel.noActiveLevels'
        );
    } else if (bulkUserSubs.length === 0) {
        bulkLevelDisabledReason =
            crossOrgSelectedCount > 0
                ? t('console.hierarchy.scopedAccounts.bulkLevel.crossOrgOnly')
                : t('console.hierarchy.scopedAccounts.bulkLevel.noSelection');
    }

    return {
        enabled,
        canView,
        canManage,
        canReset,

        orgId,
        org,
        orgName: org?.name ?? null,
        orgQuery,
        orgNameById,
        activeLevels,

        filters,
        // EAA-1820: exposed (not just `patchFilters`) so the shared screen's
        // "Lưu bộ lọc hiện tại" can restore a WHOLE snapshot in one call —
        // every individual `select*` helper only ever changes one field.
        setFilters,
        filtersActive,
        toggleIncludeChildren,
        facetGroups,
        selectFacet,
        clearFilters,

        listQuery,
        rows,
        totalCount,
        loading: listQuery.isLoading || orgQuery.isLoading,
        isError: listQuery.isError || orgQuery.isError,
        error: (listQuery.error ?? orgQuery.error) as unknown,
        invalidateList,

        page,
        setPage,
        pageSize,
        setPageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalPages,
        rangeFrom,
        rangeTo,

        sortField,
        sortDirection,
        toggleSort,

        selected,
        selectedRows,
        selectedCount: selected.size,
        pageSelectedCount,
        allPageSelected,
        somePageSelected,
        toggleRow,
        toggleAll,
        clearSelection,
        selectAllMatching,
        selectAllBusy,
        selectAllCapped,
        maxSelectAllRows: MAX_SELECT_ALL_PAGES * MAX_SERVER_PAGE_SIZE,

        bulkUserSubs,
        crossOrgSelectedCount,
        bulkLevelDisabledReason,
    };
}

export type ScopedAccountsVM = ReturnType<typeof useScopedAccounts>;
